import { Banner, Button, Spinner, StatusBadge } from '@immo/ui-kit';
import type { ReactNode } from 'react';
import {
  isFleetHostCommandVerb,
  isFleetLaneCommandVerb,
  type FleetCommandView,
  type FleetHostCommandVerb,
  type FleetLaneCommandVerb,
  type FleetJobView,
  type FleetLogLineStream,
  type FleetLogReadLimit,
  type ReadFleetLogResponse,
  type FleetMachineOverview,
  type FleetProfileCapacity,
  type FleetProfileState,
} from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';
import { useLocaleFormat } from '@/i18n/use-locale-format';
import { LockIcon, ServerIcon, TerminalIcon } from '@/components/layout/icons';
import { FleetApprovalBadge } from './fleet-badges';
import { useCommandVerbCopy, useVerbLabel, type FormCommandVerb, type MachineCommandVerb } from './command-verbs';
import { Last24HoursCard } from './health/last-24-hours-card';
import { PanelHealth } from './health/panel-health';
import { FleetThrottleBadge } from './health/throttle-badge';
import { ProfileCard } from './profile-card';
import { ProfileCommands } from './profile-commands';
import { LogViewer } from './log-viewer';
import { CommandActivity } from './command-activity';
import { useCommandStepLabel } from './command-outcome-badge';
import { commandStep, type FleetCommandStep } from './command-steps';
import { MachineAccounts } from './machine-accounts';
import { JobPulse, SuspectNotice } from './job-pulse';

export type ViewerState = {
  readonly open: boolean;
  readonly stream: FleetLogLineStream;
  readonly limit: FleetLogReadLimit;
  /** What the last read returned, oldest first — `null` until the first read resolves. */
  readonly log: ReadFleetLogResponse | null;
  readonly loading: boolean;
};

type Props = {
  readonly machine: FleetMachineOverview;
  readonly canCommand: boolean;
  readonly commands: ReadonlyArray<FleetCommandView>;
  readonly viewers: Readonly<Record<string, ViewerState>>;
  /** Each lane's fleet-wide cap and the other machines running it, keyed by lane — absent for an uncapped lane. */
  readonly capacities?: Readonly<Record<string, FleetProfileCapacity>>;
  readonly onApprove: () => void;
  readonly onReject: () => void;
  readonly onRevoke: () => void;
  /** A verb against the whole host — issued once per machine, never per lane. */
  readonly onHostVerb: (verb: MachineCommandVerb) => void;
  /** `start` / `stop` / `restart` on one lane of this host (fleet-agent ADR 0013). */
  readonly onLaneVerb: (profile: string, verb: FleetLaneCommandVerb) => void;
  /** Opens a form verb's dialog: `run-command`, `e2e-unlock` or `system-preview` — each followed until it settles. */
  readonly onFormVerb?: (verb: FormCommandVerb) => void;
  /**
   * Opens the `engine` dialog — `null` for an empty lane to type, a lane to pin, with that lane's own pin
   * preselected when it has one. Host-wide on the wire.
   */
  readonly onEngine: (lane: string | null, current: FleetMachineOverview['profiles'][number]['engine']) => void;
  readonly onToggleViewer: (profile: string) => void;
  readonly onStreamChange: (profile: string, stream: FleetLogLineStream) => void;
  readonly onLimitChange: (profile: string, limit: FleetLogReadLimit) => void;
  readonly onRefreshViewer: (profile: string) => void;
  readonly onCleanLogs: (profile: string) => void;
  /** The fleet's live jobs (fleet-agent ADR 0012): each lane shows the one its worker holds on this machine. */
  readonly jobs?: ReadonlyArray<FleetJobView>;
  /** After a suspect is cleared or its job released from a lane card. */
  readonly onJobsChanged?: () => void | Promise<void>;
};

/**
 * Every approval control lives here, gated on the `canCommand` prop the page
 * derives from `backoffice:fleet:command` — absent, never disabled, when the
 * grant is missing. Control matrix: `pending` → [Ablehnen] [Freigeben];
 * `approved` → [Zugang entziehen]; `rejected` / `revoked` → [Freigeben] — the
 * backend re-admits either of those on approve (IMM-910.1).
 */
export function MachineDetail({
  machine,
  canCommand,
  commands,
  viewers,
  capacities = {},
  onApprove,
  onReject,
  onRevoke,
  onHostVerb,
  onLaneVerb,
  onFormVerb = () => undefined,
  onEngine,
  onToggleViewer,
  onStreamChange,
  onLimitChange,
  onRefreshViewer,
  onCleanLogs,
  jobs = [],
  onJobsChanged = () => undefined,
}: Props) {
  const t = useTranslations('pages.admin.fleet');
  const format = useLocaleFormat();
  const approved = machine.approval === 'approved';

  return (
    // One scroll context, the page's own — not a pinned header over an inner
    // scroller. The facts block is tall on a phone, and holding it fixed left
    // almost no room for the lanes, which is what the page is for.
    <div className="flex flex-col">
      <div className="border-b border-border bg-surface px-4 py-4 sm:px-6 sm:py-5">
        {/* One DOM at every width, top to bottom: the badges with the enrolment action, the health, the host
            commands in three rows, then the facts. The machine name is never repeated here — the side panel's
            header carries it on a desktop, the topbar beside the back chevron on a phone (OPS-67). */}
        <div className="flex min-w-0 flex-col gap-4">
          <div className="flex min-w-0 flex-wrap items-center gap-2 sm:gap-3">
            <FleetApprovalBadge approval={machine.approval} />
            {approved && machine.health?.throttle != null && <FleetThrottleBadge throttle={machine.health.throttle} />}
            {machine.stale && <StatusBadge label={t('stale')} tone="danger" dot />}
            {canCommand && (
              <div className="ml-auto">
                <DetailActions
                  approval={machine.approval}
                  onApprove={onApprove}
                  onReject={onReject}
                  onRevoke={onRevoke}
                />
              </div>
            )}
          </div>

          {approved && (
            <>
              <PanelHealth
                health={machine.health ?? null}
                canCommand={canCommand}
                hostCommandPending={pendingHostCommandFor(commands) !== null}
                onSystemPreview={() => onFormVerb('system-preview')}
                onSystemPrune={() => onHostVerb('system-prune')}
              />
              <Last24HoursCard machineId={machine.id} health={machine.health ?? null} />
            </>
          )}

          {approved && canCommand && (
            <HostCommands
              profiles={machine.profiles.map((profile) => profile.state)}
              pending={pendingHostCommandFor(commands)}
              onVerb={onHostVerb}
              onFormVerb={onFormVerb}
              onEngine={() => onEngine(null, null)}
            />
          )}

          {/* Label/value pairs on a grid — two columns in the side panel, one on a phone. A wrapping row breaks
              ragged and stops reading as pairs. Only facts about the HOST: a checkout belongs to one of the
              projects the host serves, so none is shown here. */}
          <dl className="grid grid-cols-1 gap-x-6 gap-y-1.5 sm:grid-cols-2">
            {machineFacts(machine, t, format.ago).map(([label, value, display]) => (
              <div key={label} className="flex min-w-0 items-baseline gap-3">
                <dt className="w-32 shrink-0 text-sm text-text-tertiary">{label}</dt>
                <dd className="min-w-0 truncate font-mono text-base-sm text-text-primary" title={value}>
                  {display ?? value}
                </dd>
              </div>
            ))}
          </dl>

          {/* Absent until an agent that reports accounts ticks — an empty list would claim the host runs on none. */}
          {machine.accounts !== undefined && machine.accounts.length > 0 && (
            <MachineAccounts accounts={machine.accounts} />
          )}
        </div>
      </div>

      <div className="px-4 py-4 sm:px-6 sm:py-5">
        <div className="flex flex-col gap-3 sm:gap-4">
          {machine.approval === 'pending' && (
            <Banner variant="warning" title={t('join-request-title')} description={t('join-request-body')} />
          )}
          {machine.stale && (
            <Banner
              variant="danger"
              title={t('stale')}
              description={t('stale-note', { time: format.ago(machine.lastSeenAt) })}
            />
          )}
          {!canCommand && <Banner variant="info" title={t('read-only-note')} />}
          {machine.profiles.length === 0 ? (
            <div className="rounded-lg border border-dashed border-neutral-300 bg-surface p-6 text-center text-base-sm text-text-placeholder">
              {t('no-profiles')}
            </div>
          ) : (
            machine.profiles.map((profile) => {
              const viewer = viewers[profile.profile];
              const job =
                profile.worker == null
                  ? undefined
                  : jobs.find((candidate) => candidate.machineId === machine.id && candidate.worker === profile.worker);

              return (
                <ProfileCard
                  key={profile.profile}
                  profile={profile}
                  stale={machine.stale}
                  capacity={capacities[profile.profile] ?? null}
                  pulse={
                    job === undefined ? undefined : (
                      <div className="flex flex-col gap-2">
                        <JobPulse job={job} className="text-sm" />
                        <SuspectNotice job={job} canCommand={canCommand} onChanged={onJobsChanged} />
                      </div>
                    )
                  }
                  footer={
                    <ProfileCommands
                      approved={approved}
                      viewerOpen={viewer?.open ?? false}
                      canCommand={canCommand}
                      profile={profile}
                      pending={pendingLaneCommandFor(commands, profile.profile)}
                      onLaneVerb={(verb) => onLaneVerb(profile.profile, verb)}
                      onEngine={() => onEngine(profile.profile, profile.engine)}
                      onToggleViewer={() => onToggleViewer(profile.profile)}
                    />
                  }
                >
                  {viewer?.open === true && (
                    <LogViewer
                      readOnly={!canCommand}
                      stream={viewer.stream}
                      limit={viewer.limit}
                      log={viewer.log}
                      loading={viewer.loading}
                      onStreamChange={(stream) => onStreamChange(profile.profile, stream)}
                      onLimitChange={(limit) => onLimitChange(profile.profile, limit)}
                      onRefresh={() => onRefreshViewer(profile.profile)}
                      {...(canCommand && profile.worker != null
                        ? { onCleanLogs: () => onCleanLogs(profile.profile) }
                        : {})}
                    />
                  )}
                </ProfileCard>
              );
            })
          )}

          <CommandActivity commands={commands} />
        </div>
      </div>
    </div>
  );
}

/** The machine's facts, in reading order — label, plain value, and an optional rendering of it; the approval only once reported. */
function machineFacts(
  machine: FleetMachineOverview,
  t: (key: string, values?: Record<string, string>) => string,
  ago: (iso: string) => string,
): ReadonlyArray<readonly [string, string, ReactNode?]> {
  return [
    [t('host'), machine.hostname],
    [t('platform'), machine.platform],
    [t('agent-version'), machine.agentVersion, <AgentRelease key="release" version={machine.agentVersion} />],
    [t('first-seen'), ago(machine.firstSeenAt)],
    [t('last-seen'), ago(machine.lastSeenAt)],
    ...(machine.approvedByName !== null && machine.approvedAt !== null
      ? [[t('approved-by'), `${machine.approvedByName} · ${ago(machine.approvedAt)}`] as const]
      : []),
    [t('public-key'), shortKey(machine.publicKey)],
  ];
}

/**
 * The installed fleet-agent release, linked to its GitHub release. From 0.21.0
 * the host reports it on every tick; an older host shows what it enrolled with.
 */
function AgentRelease({ version }: { readonly version: string }) {
  return (
    <a
      href={`${FLEET_AGENT_RELEASES_URL}/v${version}`}
      target="_blank"
      rel="noreferrer noopener"
      className="text-interactive hover:underline"
    >
      {version}
    </a>
  );
}

const FLEET_AGENT_RELEASES_URL = 'https://github.com/hlrealestate/fleet-agent/releases/tag';

/** The key's ends, enough to tell two machines apart; the whole key is never something an operator reads. */
function shortKey(key: string): string {
  return key.length <= 16 ? key : `${key.slice(0, 8)}…${key.slice(-8)}`;
}

function DetailActions({
  approval,
  onApprove,
  onReject,
  onRevoke,
}: {
  readonly approval: FleetMachineOverview['approval'];
  readonly onApprove: () => void;
  readonly onReject: () => void;
  readonly onRevoke: () => void;
}) {
  const t = useTranslations('pages.admin.fleet');

  if (approval === 'pending') {
    return (
      <div className="grid w-full shrink-0 grid-cols-2 gap-2 sm:flex sm:w-auto">
        <Button size="sm" variant="secondary" className="w-full sm:w-auto" onClick={onReject}>
          {t('reject-cta')}
        </Button>
        <Button size="sm" className="w-full sm:w-auto" onClick={onApprove}>
          {t('approve-cta')}
        </Button>
      </div>
    );
  }

  if (approval === 'approved') {
    return (
      <Button size="sm" variant="ghost" className="w-full sm:w-auto" onClick={onRevoke}>
        {t('revoke-cta')}
      </Button>
    );
  }

  return (
    <Button size="sm" onClick={onApprove}>
      {t('approve-cta')}
    </Button>
  );
}

/**
 * The verbs that act on the whole machine rather than one lane or project:
 * `restart-agent` restarts the one agent process, `update-agent` updates and
 * restarts it (its dialog can force it, which resets the clone first), and
 * `start-all` / `stop-all` / `restart-all` start, stop and cycle the host —
 * every lane it serves. All of them are rendered once per machine because that
 * is how often they act; one lane's own verbs live on its card (ADR 0013).
 *
 * `sync-branch` and `clean-checkout` stay in the contract but get no button:
 * they act on one project's checkout, and a host may serve several.
 *
 * `start-all` and `stop-all` are mutually exclusive on the machine's own
 * reported state: a machine
 * with any lane running offers `stop-all`, a machine with none offers
 * `start-all`. That is two buttons of which only one is ever applicable, not
 * one button with a mode — showing the inapplicable one invites a command the
 * executor would answer `already-running`.
 *
 * A machine reporting no profiles at all gets neither: there is nothing to
 * start and nothing to stop, and `startAllLanes` refuses an empty observation
 * list rather than reporting success for a host whose state nobody could read.
 *
 * Replaced by a spinner, not disabled, while one is in flight: a second
 * `start-all` before the first settles is a command the host would answer
 * `already-running`, which is noise, not information.
 */
function HostCommands({
  profiles,
  pending,
  onVerb,
  onFormVerb,
  onEngine,
}: {
  readonly profiles: ReadonlyArray<FleetProfileState>;
  readonly pending: FleetCommandView | null;
  readonly onVerb: (verb: MachineCommandVerb) => void;
  readonly onFormVerb: (verb: FormCommandVerb) => void;
  readonly onEngine: () => void;
}) {
  if (pending !== null) {
    return (
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        <PendingHostVerb verb={pending.verb as MachineCommandVerb} step={commandStep(pending)} />
      </div>
    );
  }

  const allLanesVerb = allLanesVerbFor(profiles);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        {allLanesVerb !== null && <HostVerbButton verb={allLanesVerb} onClick={() => onVerb(allLanesVerb)} />}
        {/* Offered beside whichever of the pair applies: it starts a stopped lane and cycles a running one. */}
        {allLanesVerb !== null && <HostVerbButton verb="restart-all" onClick={() => onVerb('restart-all')} />}
        <HostVerbButton verb="engine" onClick={onEngine} />
      </div>
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        {MACHINE_VERBS.map((verb) => (
          <HostVerbButton key={verb} verb={verb} onClick={() => onVerb(verb)} />
        ))}
      </div>
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        {FORM_VERBS.map((verb) => (
          <FormVerbButton key={verb} verb={verb} onClick={() => onFormVerb(verb)} />
        ))}
      </div>
    </div>
  );
}

/**
 * The machine verbs with a button besides the start/stop pair; `force-update-agent` rides `update-agent`'s dialog.
 * `system-prune` (fleet-agent ADR 0018) sits in the panel's health section beside the disk it frees.
 */
const MACHINE_VERBS = ['restart-agent', 'update-agent'] as const satisfies ReadonlyArray<MachineCommandVerb>;

/**
 * `stop-all` while any lane runs, `start-all` when none does, and `null` for a
 * machine with no lanes to command. A lane counts as running unless it reports
 * `stopped` — `pending`, `idle`, `working` and `waiting-on-human` all mean a
 * daemon holds the loop lock.
 */
function allLanesVerbFor(profiles: ReadonlyArray<FleetProfileState>): FleetHostCommandVerb | null {
  if (profiles.length === 0) return null;

  return profiles.some((state) => state !== 'stopped') ? 'stop-all' : 'start-all';
}

function HostVerbButton({
  verb,
  onClick,
}: {
  readonly verb: MachineCommandVerb | 'engine';
  readonly onClick: () => void;
}) {
  const copy = useCommandVerbCopy(verb);
  const Icon = copy.icon;

  return (
    <Button type="button" size="sm" variant={copy.variant} className="w-full sm:w-auto" onClick={onClick}>
      <Icon className="h-4 w-4" aria-hidden />
      {copy.label}
    </Button>
  );
}

/** The form verbs in the tools row; `system-preview` sits in the panel's health section. None deletes anything. */
const FORM_VERBS = ['run-command', 'e2e-unlock'] as const satisfies ReadonlyArray<FormCommandVerb>;

const FORM_VERB_ICONS = {
  'run-command': TerminalIcon,
  'e2e-unlock': LockIcon,
  'system-preview': ServerIcon,
} as const satisfies Record<FormCommandVerb, unknown>;

/** Not a confirmable verb: its dialog is a form that then follows the command (`followed-command.tsx`). */
function FormVerbButton({ verb, onClick }: { readonly verb: FormCommandVerb; readonly onClick: () => void }) {
  const label = useVerbLabel(verb);
  const Icon = FORM_VERB_ICONS[verb];

  return (
    <Button type="button" size="sm" variant="secondary" className="w-full sm:w-auto" onClick={onClick}>
      <Icon className="h-4 w-4" aria-hidden />
      {label}
    </Button>
  );
}

function PendingHostVerb({ verb, step }: { readonly verb: MachineCommandVerb; readonly step: FleetCommandStep }) {
  const copy = useCommandVerbCopy(verb);
  const stepLabel = useCommandStepLabel(step);

  return (
    <span className="flex items-center gap-2 text-base-sm text-text-tertiary" role="status">
      <Spinner size="sm" aria-hidden />
      {copy.label} — {stepLabel}
    </span>
  );
}

/**
 * The machine's oldest unsettled host command, or null. `accepted` is the only
 * unsettled outcome — every other value means the machine answered
 * (IMM-910.5). Read from the same `commands` array the feed renders, so the
 * spinner and the feed can never disagree about what is in flight, nor about
 * its step (OPS-57).
 */
function pendingHostCommandFor(commands: ReadonlyArray<FleetCommandView>): FleetCommandView | null {
  return (
    commands.find(
      (command) =>
        command.outcome === 'accepted' && (isFleetHostCommandVerb(command.verb) || command.verb === 'system-prune'),
    ) ?? null
  );
}

/** The lane's unsettled `start` / `stop` / `restart`, read from the same array the feed renders. */
function pendingLaneCommandFor(commands: ReadonlyArray<FleetCommandView>, profile: string): FleetCommandView | null {
  return (
    commands.find(
      (command) =>
        command.outcome === 'accepted' && command.profile === profile && isFleetLaneCommandVerb(command.verb),
    ) ?? null
  );
}
